import Backbone from 'backbone';
import _ from 'underscore';
import PageApp from 'pageApp';
import GetProperty from '../util/PropertyGetter';
import KillSwitchUtil from './../util/KillSwitchUtil';

const ThumbnailModel = Backbone.Model.extend({

  defaults: {
    loaded: false,
  },

  url() {
    let url;
    const colorName = this.get('colorName');
    if (KillSwitchUtil.isTrue('dbedClientIdParamRemovalEnabled')) {
      url = `/xapi/digital/v1/product/${this.id}?size=small&${ThumbnailModel.contextParams}`;
    } else {
      url = `/xapi/digital/v1/product/${this.id}?size=small&clientId=PROS&${ThumbnailModel.contextParams}`;
    }

    if (colorName) {
      const colName = encodeURIComponent(colorName);
      url += `&swatchColor=${colName}`;
    }


    return url;
  },
  parse(response) {
    if (!(response.product && response.product.length) ||
        (!response.product[0].availability.available && !this.attributes.singleFetch)) {
      return undefined;
    }
    const responseProduct = response.product[0];

    const product = Object.assign(responseProduct, {
      imagery: this.mapImagery(responseProduct),
      detail: this.mapDetail(responseProduct),
      pricing: this.mapPricing(responseProduct),
      meta: this.mapMeta(response),
      traits: this.mapTraits(responseProduct),
      imageWidth: this.mapImageWidth(responseProduct),
    });

    if (this.get('zone') === 'SB_ZONE_UNAVAILABLE') {
      responseProduct.zone = this.get('zone');
    }
    // setup final price flag, parse on template to apply different behavior within redesign pros
    const badges = GetProperty(product, 'pricing.badges', false);
    if (badges) {
      product.detail.flags.hasFinalPrice = badges.find(b => b.finalPrice) || false;
      const { tieredPrice } = product.pricing.price;
      if (product.detail.flags.hasFinalPrice && tieredPrice.length > 1) {
        tieredPrice[1].values.forEach((price) => {
          price.type = 'regular'; // eslint-disable-line no-param-reassign
        });
      }
    }

    return { product };
  },
  isValid() {
    return this.attributes.product &&
      this.attributes.product.identifier &&
      this.attributes.product.detail &&
      this.attributes.product.availability &&
      this.attributes.product.availability.available &&
      this.attributes.product.pricing;
  },

  mapImagery(product) {
    const { imagery } = product;
    const properties = this.get('properties');
    const primaryImage = (properties && properties.prosFacetFilterPhase2Enabled) ?
      (this.mapUpcImagery(product) || imagery.images[0]) : imagery.images[0];
    // Adding width to Primary Image for homepage zone
    if (ThumbnailModel.hpZoneFlag) {
      primaryImage.width = 296;
    }
    return {
      primaryImage,
      additionalImageSource: imagery,
    };
  },

  mapUpcImagery(product) {
    const recommendedUpcIds = this.get('upcs') ? this.get('upcs') : [];
    let upcImage;
    if (this.get('displayUpcImage') && recommendedUpcIds.length > 0) {
      const productUpcs = product.relationships ? product.relationships.upcs : {};
      for (let i = 0, len = recommendedUpcIds.length; i < len; i += 1) {
        const selectedUpc = productUpcs[recommendedUpcIds[i]];
        if (selectedUpc && selectedUpc.availability && selectedUpc.availability.available) {
          if (selectedUpc.traits && selectedUpc.traits.colors && selectedUpc.traits.colors.selectedColor) {
            const selectedColorId = selectedUpc.traits.colors.selectedColor;
            if (product.traits && product.traits.colors && product.traits.colors.colorMap) {
              const colorMap = product.traits.colors.colorMap[selectedColorId];
              if (colorMap && colorMap.imagery && colorMap.imagery.images) {
                // eslint-disable-next-line prefer-destructuring
                upcImage = colorMap.imagery.images[0];
                break;
              }
            }
          }
        }
      }
    }
    return upcImage;
  },
  mapDetail(product) {
    const { detail } = product;
    if (detail.brand) {
      detail.brand = detail.brand.name;
      if (!(/CHANEL/i.test(detail.brand))) {
        if (!PageApp.getMeta('properties.isThumbnailRedesignEnabled')) {
          detail.name = `${detail.brand} ${detail.name}`;
        }
      }
    }
    return detail;
  },
  mapPricing(product) {
    const { pricing } = product;
    const wasPriceLabel = 'was [price]';
    if (pricing) {
      // remove was price
      if (pricing.price && pricing.price.tieredPrice && pricing.price.tieredPrice.length > 0) {
        pricing.price.tieredPrice = _.filter(pricing.price.tieredPrice, priceObj =>
          (priceObj && priceObj.label && priceObj.label.toLowerCase() !== wasPriceLabel.toLowerCase()));
      }

      return Object.assign(pricing, this.mapBadges(product));
    }

    return undefined;
  },

  mapMeta(response) {
    const meta = GetProperty(response, 'meta', {});
    meta.properties = PageApp.getMeta('properties');
    return meta;
  },

  mapTraits(product) {
    const { traits } = product;
    let urlTemplate;
    const properties = PageApp.getMeta('properties');
    if (properties.snbTemplateUrlRemovalEnabled) {
      urlTemplate = {
        product: properties.productUrlTemplate,
        swatch: properties.swatchUrlTemplate,
      };
    } else if (product.meta && product.meta.properties) {
      urlTemplate = {
        product: product.meta.properties.productUrlTemplate,
        swatch: product.meta.properties.swatchUrlTemplate,
      };
    } else {
      urlTemplate = product.urlTemplate;
    }
    if (traits && traits.colors && traits.colors.colorMap && _.isObject(traits.colors.colorMap) && urlTemplate) {
      const colorsArr = Object.values(traits.colors.colorMap);
      traits.colors.colorMap = colorsArr.map((color) => {
        const mapedColor = Object.assign({}, color);
        mapedColor.imagery.primaryImage = GetProperty(color, 'imagery.images', [])[0] || {};
        return mapedColor;
      });

      traits.colors.urlTemplate = urlTemplate;
    }

    return traits;
  },

  mapImageWidth() {
    const imageWidth = this.get('imageWidth');
    if (imageWidth) {
      return imageWidth;
    }
    return undefined;
  },

  mapBadges(product) {
    const finalPrice = product.pricing.price.finalPrice || {};
    let topBadges = this.getTopBadges(product);
    if (!_.isEmpty(finalPrice)) {
      topBadges = this.badgeWithFinalPrice(topBadges, finalPrice);
    }
    return { badges: topBadges };
  },

  badgeWithFinalPrice(topBadges, finalPrice) {
    const position = this.getFinalPricePosition(topBadges, finalPrice);
    const secondBadge = (topBadges.length > 1) ? topBadges[1].header : '';
    // if product have finalPrice applicable promotions position should be greater than 0
    if (position) {
      if (position === 2 && secondBadge && secondBadge[0] === secondBadge[0].toUpperCase()) {
        topBadges[1].header = this.formatSecondBadge(secondBadge); // eslint-disable-line no-param-reassign
      }
      topBadges.forEach((badge, index) => {
        badge.type = (index < position) ? 'discount' : 'regular'; // eslint-disable-line no-param-reassign
      });
      topBadges.splice(position, 0, { finalPrice }); // eslint-disable-line no-param-reassign
    }
    return topBadges;
  },

  getTopBadges(product) {
    const { badgeIds = [], badgesMap = {} } = product.pricing;
    return badgeIds.map(id => badgesMap[id]).slice(0, 2);
  },

  getFinalPricePosition(topBadges, finalPrice) {
    const { applicablePromotions = [] } = finalPrice;
    const topBadgesPromoIds = _.pluck(topBadges, 'promoId');
    let finalPricePosition = 0;
    if (topBadgesPromoIds.length >= applicablePromotions.length) {
      finalPricePosition = _.intersection(topBadgesPromoIds, applicablePromotions).length;
    }
    return (finalPricePosition === applicablePromotions.length) ? finalPricePosition : 0;
  },
  formatSecondBadge(secondBadgeHeader) {
    return `Plus ${secondBadgeHeader.charAt(0).toLowerCase()}${secondBadgeHeader.slice(1)}`;
  },
});

export default ThumbnailModel;
